import {
  apptHasValidStates,
  getConfirmedStates,
  isAgentAppointedWithCarrier,
  isStateInProgress,
  isAppointmentActionable,
  isRecertificationRequest
} from 'helpers/appointments/appointments';
import { getCompany } from 'helpers/getCompanyVariables';

const company = getCompany();

const WISCONSIN = 'Wisconsin';
const DEAN_HEALTHPLAN_BY_MEDICA = 'Dean HealthPlan by Medica';
const MEDICA = 'Medica';

function shouldIgnoreStateSubform(appt, user) {
  const isAppointed = isAgentAppointedWithCarrier(appt.carrier, user);
  return (
    !appt.consider_states ||
    // agent has to be appointed in order to consider the recertification valid
    (isAppointed && isRecertificationRequest(appt)) ||
    (appt.enableStateRequestsIfAppointed && // carrier does not consider states
      !isAppointed) // agent is not appointed
  );
}

export function sanitizeApptRequests(carriersByType, appts, user) {
  const sanitized = appts
    .filter(appt => {
      const carriers = carriersByType[appt.carrierType];

      if (
        !carriers ||
        typeof carriers !== 'object' ||
        Object.keys(carriers).length === 0 ||
        carriers[appt.carrier] === undefined
      ) {
        return false;
      }

      const excludedCarrier =
        !!carriers[appt.carrier].exclude ||
        !!carriers[appt.carrier].agencyDisabled;

      // carrier is excluded
      if (excludedCarrier) return false;

      if (shouldIgnoreStateSubform(appt, user)) return true;

      return apptHasValidStates(appt);
    })
    .map(appt => {
      if (shouldIgnoreStateSubform(appt, user)) {
        return {
          ...appt,
          appointments_information: []
        };
      }

      // get the states where the agent is appointed
      const confirmedStates = getConfirmedStates(appt.carrier, user);

      return {
        ...appt,
        appointments_information: (appt?.appointments_information || []).filter(
          row =>
            !!row.Appointment_State_1 && // state has to exist
            row.Parent_Id?.name === appt.requestName && // state should be part of the original request (Zoho's fault)
            !confirmedStates.includes(row.Appointment_State_1) && // agent isn't appointed in that state
            isStateInProgress(row) // state has to be in progress
        )
      };
    })
    .flatMap(appt => {
      // Sunshine + Medica: split Wisconsin into a separate "Dean HealthPlan by Medica" appointment
      if (company !== 'sunshine' || appt.carrier !== MEDICA) return [appt];
      if (shouldIgnoreStateSubform(appt, user)) return [appt];

      const info = appt.appointments_information || [];
      const wisconsinRows = info.filter(
        row => row.Appointment_State_1 === WISCONSIN
      );
      const otherRows = info.filter(
        row => row.Appointment_State_1 !== WISCONSIN
      );

      if (wisconsinRows.length === 0) return [appt];

      const medicaWithoutWisconsin = {
        ...appt,
        appointments_information: otherRows
      };
      const deanWithWisconsin = {
        ...appt,
        carrier: DEAN_HEALTHPLAN_BY_MEDICA,
        appointments_information: wisconsinRows
      };
      return [medicaWithoutWisconsin, deanWithWisconsin];
    })
    .filter(
      appt =>
        shouldIgnoreStateSubform(appt, user) ||
        appt.appointments_information.length > 0
    );

  // DEDUPE STATES PER CARRIER
  const stateIndex = new Map();

  sanitized.forEach(appt => {
    if (shouldIgnoreStateSubform(appt, user)) return;

    appt.appointments_information.forEach(row => {
      const state = row.Appointment_State_1;
      const key = `${appt.carrier}-${state}`;
      const existing = stateIndex.get(key);

      if (!existing) {
        stateIndex.set(key, { appt, row });
        return;
      }

      const currentIsActionable = isAppointmentActionable(appt);
      const existingIsActionable = isAppointmentActionable(existing.appt);

      // priority 1: actionable
      if (currentIsActionable && !existingIsActionable) {
        stateIndex.set(key, { appt, row });
        return;
      }

      if (!currentIsActionable && existingIsActionable) {
        return;
      }

      // priority 2: most recent created_time
      const currentTime = new Date(appt.created_time).getTime();
      const existingTime = new Date(existing.appt.created_time).getTime();

      if (currentTime > existingTime) {
        stateIndex.set(key, { appt, row });
      }
    });
  });

  // rebuild appointments with deduped states
  const groupedByAppt = new Map();

  stateIndex.forEach(({ appt, row }) => {
    if (!groupedByAppt.has(appt)) {
      groupedByAppt.set(appt, {
        ...appt,
        appointments_information: []
      });
    }

    groupedByAppt.get(appt).appointments_information.push(row);
  });

  return sanitized
    .map(appt => {
      if (shouldIgnoreStateSubform(appt, user)) return appt;
      return (
        groupedByAppt.get(appt) || { ...appt, appointments_information: [] }
      );
    })
    .filter(
      appt =>
        shouldIgnoreStateSubform(appt, user) ||
        appt.appointments_information.length > 0
    );
}
